import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { Download, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'

import type { TransactionPayload } from '@ankor-io/common/types/transaction/transaction'

import { useUserContext } from '@/providers/user/useUserContext'
import { Button } from '@/ui-components/Button'
import { Heading } from '@/ui-components/Heading'
import Loading from '@/ui-components/Loading'
import { getTransactionQueryParams } from '@/utils/getQueryParams/getTransactionQueryParams'
import AccountTransactionsTable from '@/views/Transactions/AccountTransactionsTable'
import TransactionFiltersComponent, { type TransactionFilters } from '@/views/components/TransactionFilters'

const TRANSACTIONS_PER_PAGE = 100
const SKIP = 0

const TransactionsPage: React.FC = () => {
  const { getAccessToken } = useKindeAuth()
  const { activeOrganizationId, activeConnectedAccountId } = useUserContext()

  const [isLoading, setIsLoading] = useState(true)
  const [isUpdatingFilters, setIsUpdatingFilters] = useState(false)

  const [transactions, setTransactions] = useState<TransactionPayload[]>([])
  const [transactionFilters, setTransactionFilters] = useState<TransactionFilters>({
    dateFrom: '',
    dateTo: '',
    status: '',
    categoryId: '',
    type: '',
  })

  useEffect(() => {
    const abortController = new AbortController()

    const fetchTransactions = async () => {
      try {
        if (!isUpdatingFilters) {
          setIsLoading(true)
        }

        const queryParams = getTransactionQueryParams(TRANSACTIONS_PER_PAGE, SKIP, transactionFilters)
        const res = await fetch(
          `/api/pay/org/${activeOrganizationId}/connected-account/${activeConnectedAccountId}/transactions?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${await getAccessToken()}`,
            },
            signal: abortController.signal,
          },
        )

        if (!res.ok) {
          throw new Error(`Failed to fetch transactions: ${res.status}`)
        }

        const data = (await res.json()) as TransactionPayload[]
        setTransactions(data)
      } catch (error) {
        if (!(error instanceof Error && error.name === 'AbortError')) {
          console.error('Error fetching transactions:', error)
          toast.error('Error fetching expenses')
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    fetchTransactions()

    return () => abortController.abort()
    // Don't want to re-fetch transactions when isUpdatingFilters flag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId, activeConnectedAccountId, getAccessToken, transactionFilters])

  const handleUpdatedFilters = (filters: TransactionFilters) => {
    setIsUpdatingFilters(true)
    setTransactionFilters(filters)
    setTimeout(() => {
      setIsUpdatingFilters(false)
    }, 1000)
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div className="flex md:items-center justify-between flex-col md:flex-row gap-4">
        <Heading level={3}>Expenses</Heading>
        <div className="flex gap-2 w-full md:w-auto">
          <Button
            outline
            className="w-full md:w-auto"
            onClick={() => {
              console.log('Export')
            }}
          >
            <Download data-slot="icon" />
            Export
          </Button>
        </div>
      </div>

      <TransactionFiltersComponent
        transactionFilters={transactionFilters}
        setTransactionFilters={handleUpdatedFilters}
      />

      <AccountTransactionsTable
        transactions={transactions}
        onUpdatedTransactions={setTransactions}
      />

      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loading />
        </div>
      ) : (
        transactions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="text-center">
              {!transactionFilters.dateFrom &&
              !transactionFilters.dateTo &&
              !transactionFilters.status &&
              !transactionFilters.categoryId &&
              !transactionFilters.type ? (
                <div>
                  <Search className="mx-auto size-12 text-gray-400 dark:text-gray-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No expenses yet</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                    Get started by making your first purchase. It will appear here once processed.
                  </p>
                </div>
              ) : (
                <div>
                  <Search className="mx-auto size-12 text-gray-400 dark:text-gray-500 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No expenses found</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                    Try adjusting your search or filter criteria to find what you're looking for.
                  </p>
                  <Button
                    outline
                    onClick={() => {
                      setTransactionFilters({
                        dateFrom: '',
                        dateTo: '',
                        status: '',
                        categoryId: '',
                        type: '',
                      })
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        )
      )}
    </div>
  )
}

export default TransactionsPage
